/**
 * Página de Estatísticas de uma Pesquisa
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../../services/staticDataConfig';

interface DistribuicaoOpcao {
  opcao_id: number;
  texto: string;
  quantidade: number;
  percentual: number;
}

interface EstatisticasPergunta {
  pergunta_id: number;
  titulo: string;
  tipo: string;
  total_respostas: number;
  media?: number;
  min?: number;
  max?: number;
  distribuicao_opcoes?: DistribuicaoOpcao[];
  respostas_texto?: string[];
}

interface Estatisticas {
  pesquisa_id: number;
  titulo: string;
  total_respostas: number;
  respostas_completas: number;
  taxa_conclusao: number;
  tempo_medio_segundos?: number;
  perguntas: EstatisticasPergunta[];
}

export function EstatisticasPesquisa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);

  const carregarEstatisticas = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/estatisticas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEstatisticas(data.dados);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    carregarEstatisticas();
  }, [carregarEstatisticas]);

  const exportarCSV = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/exportar-csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pesquisa_${id}_respostas.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar CSV');
    }
  };

  const formatarTempo = (segundos?: number) => {
    if (!segundos) return '-';
    if (segundos < 60) return `${segundos}s`;
    const minutos = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${minutos}m ${seg}s`;
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center p-5">
          <div className="spinner-border text-primary"></div>
        </div>
      </div>
    );
  }

  if (!estatisticas) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger mb-0">Erro ao carregar estatísticas</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-start justify-content-between mb-4">
        <div>
          <h2 className="mb-0">
            <i className="fas fa-chart-bar me-2"></i>
            Estatísticas
          </h2>
          <div className="text-muted">{estatisticas.titulo}</div>
        </div>
        <div>
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/admin/pesquisas')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Voltar
          </button>
          <button
            className="btn btn-success"
            onClick={exportarCSV}
          >
            <i className="fas fa-download me-2"></i>
            Exportar CSV
          </button>
        </div>
      </div>

          {/* Cards resumo */}
          <div className="row">
            <div className="col-md-3">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5 className="card-title mb-0">{estatisticas.total_respostas}</h5>
                      <small>Total de Respostas</small>
                    </div>
                    <i className="fas fa-clipboard-list fa-2x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5 className="card-title mb-0">{estatisticas.respostas_completas}</h5>
                      <small>Respostas Completas</small>
                    </div>
                    <i className="fas fa-check-circle fa-2x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5 className="card-title mb-0">{estatisticas.taxa_conclusao}%</h5>
                      <small>Taxa de Conclusão</small>
                    </div>
                    <i className="fas fa-percentage fa-2x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning text-dark">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5 className="card-title mb-0">{formatarTempo(estatisticas.tempo_medio_segundos)}</h5>
                      <small>Tempo Médio</small>
                    </div>
                    <i className="fas fa-clock fa-2x opacity-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estatísticas por pergunta */}
          {estatisticas.perguntas.map((pergunta, index) => (
            <div key={pergunta.pergunta_id} className="card mb-3">
              <div className="card-header">
                <strong>{index + 1}. {pergunta.titulo}</strong>
                <span className="badge bg-secondary ms-2">{pergunta.total_respostas} respostas</span>
              </div>
              <div className="card-body">
                
                {/* Escala Linear - mostrar média */}
                {pergunta.tipo === 'escala_linear' && pergunta.media !== undefined && (
                  <div>
                    <div className="mb-2">
                      <strong>Média: </strong>
                      <span className="badge bg-primary fs-5">{pergunta.media}</span>
                    </div>
                    <small className="text-muted">
                      Mínimo: {pergunta.min} | Máximo: {pergunta.max}
                    </small>
                  </div>
                )}

                {/* Múltipla Escolha e Checkbox - mostrar distribuição */}
                {(pergunta.tipo === 'multipla_escolha' || pergunta.tipo === 'caixas_selecao') && 
                  pergunta.distribuicao_opcoes && (
                  <div>
                    {pergunta.distribuicao_opcoes.map(opcao => (
                      <div key={opcao.opcao_id} className="mb-2">
                        <div className="d-flex justify-content-between mb-1">
                          <span>{opcao.texto}</span>
                          <span className="text-muted">{opcao.quantidade} ({opcao.percentual}%)</span>
                        </div>
                        <div className="progress" style={{ height: '20px' }}>
                          <div 
                            className="progress-bar bg-primary"
                            style={{ width: `${opcao.percentual}%` }}
                          >
                            {opcao.percentual > 10 && `${opcao.percentual}%`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Texto - mostrar amostra */}
                {(pergunta.tipo === 'texto_curto' || pergunta.tipo === 'texto_longo' || pergunta.tipo === 'email') && 
                  pergunta.respostas_texto && pergunta.respostas_texto.length > 0 && (
                  <div>
                    <h6 className="text-muted mb-2">Últimas respostas:</h6>
                    <ul className="list-group">
                      {pergunta.respostas_texto.slice(0, 5).map((texto, i) => (
                        <li key={i} className="list-group-item">
                          {texto}
                        </li>
                      ))}
                    </ul>
                    {pergunta.respostas_texto.length > 5 && (
                      <small className="text-muted">
                        Mostrando 5 de {pergunta.total_respostas} respostas
                      </small>
                    )}
                  </div>
                )}

                {/* Data - apenas contagem */}
                {pergunta.tipo === 'data' && (
                  <div className="text-muted">
                    {pergunta.total_respostas} datas informadas
                  </div>
                )}

              </div>
            </div>
          ))}

          {estatisticas.perguntas.length === 0 && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Ainda não há respostas para esta pesquisa.
            </div>
          )}

    </div>
  );
}

export default EstatisticasPesquisa;
